// Presentational building blocks, question-input renderers and result charts
// shared across PulseConnect pages.
import React, { useState } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LabelList,
} from 'recharts';
import {
  C, PulseQuestion, PulseAnswer, AnswerValue, npsCategory, npsColor,
} from './pulse';

// ── Base styles ──────────────────────────────────────────────────────────────
export const card: React.CSSProperties = {
  background: C.white, border: `1px solid ${C.border}`,
  borderRadius: '4px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
};
export const inputStyle: React.CSSProperties = {
  width: '100%', background: C.white, border: `1px solid ${C.border}`,
  borderRadius: '3px', color: C.text, fontSize: '13px', padding: '9px 11px',
  outline: 'none', fontFamily: 'system-ui', boxSizing: 'border-box',
};
export const labelStyle: React.CSSProperties = {
  fontSize: '10px', fontWeight: 700, color: C.muted, letterSpacing: '0.6px',
  textTransform: 'uppercase', display: 'block', marginBottom: '5px',
};
export const btnPrimary: React.CSSProperties = {
  background: C.navy, color: C.white, border: 'none', borderRadius: '3px',
  padding: '9px 20px', fontSize: '12.5px', fontWeight: 600, cursor: 'pointer', fontFamily: 'system-ui',
};
export const btnGold: React.CSSProperties = {
  background: C.gold, color: C.navy, border: 'none', borderRadius: '3px',
  padding: '9px 20px', fontSize: '12.5px', fontWeight: 700, cursor: 'pointer', fontFamily: 'system-ui',
};
export const btnGhost: React.CSSProperties = {
  background: C.white, color: C.textSec, border: `1px solid ${C.border}`,
  borderRadius: '3px', padding: '8px 16px', fontSize: '12px', fontWeight: 600, cursor: 'pointer', fontFamily: 'system-ui',
};

export function Banner({ type, text }: { type: 'ok' | 'err' | 'info'; text: string }) {
  const map = {
    ok: { bg: '#F0FDF4', bd: '#BBF7D0', fg: C.success },
    err: { bg: '#FEF2F2', bd: '#FECACA', fg: C.danger },
    info: { bg: '#FEFCE8', bd: '#FDE68A', fg: C.amber },
  }[type];
  return <div style={{ background: map.bg, border: `1px solid ${map.bd}`, borderRadius: '3px', padding: '9px 12px', marginBottom: '16px', fontSize: '12.5px', color: map.fg, lineHeight: 1.5 }}>{text}</div>;
}

export function LoadingScreen({ label }: { label?: string }) {
  return <div style={{ display: 'flex', height: '60vh', alignItems: 'center', justifyContent: 'center', background: C.surface }}>
    <div style={{ color: C.muted, fontSize: '13px', fontFamily: 'system-ui' }}>{label || 'Loading…'}</div>
  </div>;
}

export function StatusBadge({ status }: { status: string }) {
  const map: Record<string, { bg: string; fg: string; bd: string }> = {
    draft:      { bg: C.surface, fg: C.muted,   bd: C.border },
    published:  { bg: '#EFF6FF', fg: C.teal,    bd: '#BFDBFE' },
    closed:     { bg: '#FEF2F2', fg: C.danger,  bd: '#FECACA' },
    Completed:  { bg: '#F0FDF4', fg: C.success, bd: '#BBF7D0' },
    'In Progress': { bg: '#FEFCE8', fg: C.amber, bd: '#FDE68A' },
    'Not Started': { bg: C.surface, fg: C.muted, bd: C.border },
  };
  const s = map[status] || map.draft;
  return <span style={{ background: s.bg, color: s.fg, border: `1px solid ${s.bd}`, fontSize: '9px', fontWeight: 700, padding: '2px 8px', borderRadius: '2px', letterSpacing: '0.5px', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{status}</span>;
}

// ════════════════════════════════════════════════════════════════════════════
//  QUESTION INPUT RENDERERS  (taking a survey / preview)
// ════════════════════════════════════════════════════════════════════════════
export function QuestionInput({ question: q, value, onChange }: {
  question: PulseQuestion; value: AnswerValue | undefined; onChange: (v: AnswerValue) => void;
}) {
  switch (q.question_type) {
    case 'multiple_choice': return <MultipleChoice q={q} value={value} onChange={onChange} />;
    case 'multi_select':    return <MultiSelect q={q} value={value} onChange={onChange} />;
    case 'rating':          return <RatingInput q={q} value={value} onChange={onChange} />;
    case 'nps':             return <NPSInput value={value} onChange={onChange} />;
    case 'free_text':       return <FreeText value={value} onChange={onChange} />;
    case 'ranking':         return <RankingInput q={q} value={value} onChange={onChange} />;
    case 'matrix':
    case 'grid':            return <MatrixInput q={q} value={value} onChange={onChange} />;
    default:                return null;
  }
}

function MultipleChoice({ q, value, onChange }: { q: PulseQuestion; value?: AnswerValue; onChange: (v: AnswerValue) => void }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      {q.options.map(opt => {
        const on = value?.text === opt;
        return (
          <button key={opt} type="button" onClick={() => onChange({ text: opt })} style={{
            display: 'flex', alignItems: 'center', gap: '11px', textAlign: 'left', cursor: 'pointer',
            padding: '13px 15px', borderRadius: '4px', fontFamily: 'system-ui', fontSize: '13.5px',
            border: `1.5px solid ${on ? C.navy : C.border}`, background: on ? 'rgba(10,22,40,0.03)' : C.white,
            color: on ? C.navy : C.textSec, fontWeight: on ? 600 : 500, transition: 'all 0.1s',
          }}>
            <span style={{ width: '18px', height: '18px', borderRadius: '50%', flexShrink: 0, border: `2px solid ${on ? C.navy : C.mutedLt}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {on && <span style={{ width: '9px', height: '9px', borderRadius: '50%', background: C.navy }} />}
            </span>
            {opt}
          </button>
        );
      })}
    </div>
  );
}

function MultiSelect({ q, value, onChange }: { q: PulseQuestion; value?: AnswerValue; onChange: (v: AnswerValue) => void }) {
  const selected = value?.options || [];
  const toggle = (opt: string) => {
    const next = selected.includes(opt) ? selected.filter(o => o !== opt) : [...selected, opt];
    onChange({ options: next });
  };
  return (
    <div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {q.options.map(opt => {
          const on = selected.includes(opt);
          return (
            <button key={opt} type="button" onClick={() => toggle(opt)} style={{
              display: 'flex', alignItems: 'center', gap: '11px', textAlign: 'left', cursor: 'pointer',
              padding: '13px 15px', borderRadius: '4px', fontFamily: 'system-ui', fontSize: '13.5px',
              border: `1.5px solid ${on ? C.navy : C.border}`, background: on ? 'rgba(10,22,40,0.03)' : C.white,
              color: on ? C.navy : C.textSec, fontWeight: on ? 600 : 500,
            }}>
              <span style={{ width: '18px', height: '18px', borderRadius: '4px', flexShrink: 0, border: `2px solid ${on ? C.navy : C.mutedLt}`, background: on ? C.navy : C.white, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.white, fontSize: '12px', fontWeight: 700 }}>
                {on && '✓'}
              </span>
              {opt}
            </button>
          );
        })}
      </div>
      <div style={{ marginTop: '8px', fontSize: '11.5px', color: C.muted, fontWeight: 600 }}>{selected.length} selected</div>
    </div>
  );
}

function RatingInput({ q, value, onChange }: { q: PulseQuestion; value?: AnswerValue; onChange: (v: AnswerValue) => void }) {
  const nums: number[] = [];
  for (let i = q.rating_min; i <= q.rating_max; i++) nums.push(i);
  const cur = value?.number;
  return (
    <div>
      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
        {nums.map(n => {
          const on = cur === n;
          return (
            <button key={n} type="button" onClick={() => onChange({ number: n })} style={{
              width: '52px', height: '52px', borderRadius: '6px', cursor: 'pointer', fontFamily: 'Georgia, serif',
              fontSize: '19px', fontWeight: 600,
              border: `1.5px solid ${on ? C.gold : C.border}`, background: on ? C.navy : C.white,
              color: on ? C.gold : C.textSec, transition: 'all 0.1s',
            }}>{n}</button>
          );
        })}
      </div>
      {(q.rating_min_label || q.rating_max_label) && (
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '8px', fontSize: '11px', color: C.muted, fontWeight: 500, maxWidth: `${nums.length * 60}px` }}>
          <span>{q.rating_min_label}</span><span>{q.rating_max_label}</span>
        </div>
      )}
    </div>
  );
}

function NPSInput({ value, onChange }: { value?: AnswerValue; onChange: (v: AnswerValue) => void }) {
  const cur = value?.number;
  return (
    <div>
      <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
        {Array.from({ length: 11 }, (_, n) => {
          const on = cur === n;
          const col = npsColor(n);
          return (
            <button key={n} type="button" onClick={() => onChange({ number: n })} style={{
              width: '44px', height: '44px', borderRadius: '5px', cursor: 'pointer', fontFamily: 'system-ui',
              fontSize: '15px', fontWeight: 700,
              border: `2px solid ${on ? col : C.border}`, background: on ? col : C.white,
              color: on ? C.white : C.textSec, transition: 'all 0.1s',
            }}>{n}</button>
          );
        })}
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '8px', fontSize: '11px', color: C.muted, fontWeight: 500 }}>
        <span>Not at all likely</span><span>Extremely likely</span>
      </div>
      {cur !== undefined && cur !== null && (
        <div style={{ marginTop: '12px' }}>
          {(() => {
            const cat = npsCategory(cur);
            const meta = { promoter: { c: C.success, l: 'Promoter' }, passive: { c: C.amber, l: 'Passive' }, detractor: { c: C.danger, l: 'Detractor' } }[cat];
            return <span style={{ background: `${meta.c}14`, color: meta.c, border: `1px solid ${meta.c}44`, padding: '4px 12px', borderRadius: '3px', fontSize: '11px', fontWeight: 700, letterSpacing: '0.4px', textTransform: 'uppercase' }}>You are a {meta.l}</span>;
          })()}
        </div>
      )}
    </div>
  );
}

function FreeText({ value, onChange }: { value?: AnswerValue; onChange: (v: AnswerValue) => void }) {
  const text = value?.text || '';
  return (
    <div>
      <textarea value={text} onChange={e => onChange({ text: e.target.value })} rows={4} placeholder="Type your answer…"
        style={{ ...inputStyle, resize: 'vertical', minHeight: '96px', lineHeight: 1.55, fontSize: '13.5px' }} />
      <div style={{ textAlign: 'right', fontSize: '11px', color: C.mutedLt, marginTop: '4px' }}>{text.length} characters</div>
    </div>
  );
}

function RankingInput({ q, value, onChange }: { q: PulseQuestion; value?: AnswerValue; onChange: (v: AnswerValue) => void }) {
  // Current order defaults to the declared option order.
  const order = value?.options && value.options.length === q.options.length ? value.options : q.options;
  const [dragIdx, setDragIdx] = useState<number | null>(null);

  const move = (from: number, to: number) => {
    if (to < 0 || to >= order.length || from === to) return;
    const next = [...order];
    const [it] = next.splice(from, 1);
    next.splice(to, 0, it);
    onChange({ options: next });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
      {order.map((item, i) => (
        <div
          key={item}
          draggable
          onDragStart={() => setDragIdx(i)}
          onDragOver={e => e.preventDefault()}
          onDrop={e => { e.preventDefault(); if (dragIdx !== null) move(dragIdx, i); setDragIdx(null); }}
          onDragEnd={() => setDragIdx(null)}
          style={{
            display: 'flex', alignItems: 'center', gap: '12px', padding: '11px 13px', borderRadius: '4px',
            border: `1.5px solid ${dragIdx === i ? C.gold : C.border}`, background: dragIdx === i ? 'rgba(201,168,76,0.06)' : C.white,
            cursor: 'grab', fontFamily: 'system-ui',
          }}
        >
          <input
            type="number" min={1} max={order.length} value={i + 1}
            onChange={e => { const v = parseInt(e.target.value, 10); if (!isNaN(v)) move(i, v - 1); }}
            onClick={e => e.stopPropagation()}
            style={{ width: '46px', textAlign: 'center', border: `1px solid ${C.border}`, borderRadius: '3px', padding: '5px', fontSize: '13px', fontWeight: 700, color: C.navy, fontFamily: 'Georgia, serif' }}
          />
          <span style={{ flex: 1, fontSize: '13.5px', color: C.text, fontWeight: 500 }}>{item}</span>
          <span style={{ color: C.mutedLt, fontSize: '15px', lineHeight: 1 }}>⋮⋮</span>
        </div>
      ))}
      <div style={{ fontSize: '11px', color: C.muted, marginTop: '2px' }}>Drag to reorder, or type a rank number. 1 = highest.</div>
    </div>
  );
}

function MatrixInput({ q, value, onChange }: { q: PulseQuestion; value?: AnswerValue; onChange: (v: AnswerValue) => void }) {
  const matrix = value?.matrix || {};
  const set = (row: string, col: string) => onChange({ matrix: { ...matrix, [row]: col } });
  return (
    <div style={{ overflowX: 'auto', border: `1px solid ${C.border}`, borderRadius: '4px' }}>
      <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: `${180 + q.matrix_columns.length * 90}px`, fontFamily: 'system-ui' }}>
        <thead>
          <tr>
            <th style={{ position: 'sticky', left: 0, background: C.surface, zIndex: 2, textAlign: 'left', padding: '10px 14px', fontSize: '11px', color: C.muted, fontWeight: 700, borderBottom: `1px solid ${C.border}` }} />
            {q.matrix_columns.map(col => (
              <th key={col} style={{ padding: '10px 8px', fontSize: '11px', color: C.textSec, fontWeight: 600, textAlign: 'center', borderBottom: `1px solid ${C.border}`, background: C.surface, position: 'sticky', top: 0 }}>{col}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {q.matrix_rows.map((row, ri) => {
            const chosen = matrix[row];
            return (
              <tr key={row} style={{ background: chosen ? 'rgba(10,22,40,0.02)' : (ri % 2 ? C.white : '#FCFCFD') }}>
                <td style={{ position: 'sticky', left: 0, background: chosen ? '#F4F6FA' : C.white, zIndex: 1, padding: '11px 14px', fontSize: '12.5px', color: C.text, fontWeight: chosen ? 600 : 500, borderBottom: `1px solid ${C.borderLt}`, whiteSpace: 'nowrap' }}>{row}</td>
                {q.matrix_columns.map(col => {
                  const on = chosen === col;
                  return (
                    <td key={col} onClick={() => set(row, col)} style={{ textAlign: 'center', padding: '11px 8px', borderBottom: `1px solid ${C.borderLt}`, cursor: 'pointer' }}>
                      <span style={{ display: 'inline-flex', width: '18px', height: '18px', borderRadius: '50%', border: `2px solid ${on ? C.navy : C.mutedLt}`, alignItems: 'center', justifyContent: 'center' }}>
                        {on && <span style={{ width: '9px', height: '9px', borderRadius: '50%', background: C.navy }} />}
                      </span>
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
//  RESULT CHARTS  (results dashboard)
// ════════════════════════════════════════════════════════════════════════════
export function QuestionResult({ question: q, answers, isPB, nameFor }: {
  question: PulseQuestion; answers: PulseAnswer[]; isPB?: boolean; nameFor?: (responseId: string) => string;
}) {
  switch (q.question_type) {
    case 'multiple_choice': return <ChoiceResult q={q} answers={answers} mode="single" />;
    case 'multi_select':    return <ChoiceResult q={q} answers={answers} mode="multi" />;
    case 'rating':          return <RatingResult q={q} answers={answers} />;
    case 'nps':             return <NPSResult answers={answers} />;
    case 'free_text':       return <FreeTextResult answers={answers} isPB={isPB} nameFor={nameFor} />;
    case 'ranking':         return <RankingResult q={q} answers={answers} />;
    case 'matrix':
    case 'grid':            return <MatrixResult q={q} answers={answers} />;
    default:                return null;
  }
}

const chartTip = { background: C.navy, border: 'none', borderRadius: '4px', fontSize: '12px', color: C.white };

function ChoiceResult({ q, answers, mode }: { q: PulseQuestion; answers: PulseAnswer[]; mode: 'single' | 'multi' }) {
  const counts: Record<string, number> = {};
  q.options.forEach(o => { counts[o] = 0; });
  let respondents = 0;
  answers.forEach(a => {
    if (mode === 'single') {
      if (a.answer_text) { counts[a.answer_text] = (counts[a.answer_text] || 0) + 1; respondents++; }
    } else {
      const opts = a.answer_options || [];
      if (opts.length) respondents++;
      opts.forEach(o => { counts[o] = (counts[o] || 0) + 1; });
    }
  });
  const data = Object.entries(counts)
    .map(([name, count]) => ({ name, count, pct: respondents ? Math.round((count / respondents) * 100) : 0 }))
    .sort((a, b) => b.count - a.count);
  const maxCount = Math.max(1, ...data.map(d => d.count));

  if (respondents === 0) return <EmptyResult />;
  return (
    <div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '9px' }}>
        {data.map(d => (
          <div key={d.name}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px', fontSize: '12.5px' }}>
              <span style={{ color: C.text, fontWeight: 500 }}>{d.name}</span>
              <span style={{ color: C.muted, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{d.count} · {d.pct}%</span>
            </div>
            <div style={{ height: '10px', background: C.surface, borderRadius: '3px', overflow: 'hidden' }}>
              <div style={{ width: `${(d.count / maxCount) * 100}%`, height: '100%', background: C.navy, borderRadius: '3px' }} />
            </div>
          </div>
        ))}
      </div>
      {mode === 'multi' && <div style={{ marginTop: '10px', fontSize: '11px', color: C.mutedLt }}>Percentages of {respondents} respondents; multiple answers allowed.</div>}
    </div>
  );
}

function RatingResult({ q, answers }: { q: PulseQuestion; answers: PulseAnswer[] }) {
  const vals = answers.map(a => a.answer_number).filter((n): n is number => n !== null && n !== undefined);
  if (!vals.length) return <EmptyResult />;
  const avg = vals.reduce((s, n) => s + n, 0) / vals.length;
  const sorted = [...vals].sort((a, b) => a - b);
  const median = sorted.length % 2 ? sorted[(sorted.length - 1) / 2] : (sorted[sorted.length / 2 - 1] + sorted[sorted.length / 2]) / 2;
  const dist: { name: string; count: number }[] = [];
  for (let i = q.rating_min; i <= q.rating_max; i++) dist.push({ name: String(i), count: vals.filter(v => v === i).length });

  return (
    <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap', alignItems: 'center' }}>
      <div style={{ textAlign: 'center', minWidth: '120px' }}>
        <div style={{ fontFamily: 'Georgia, serif', fontSize: '46px', fontWeight: 600, color: C.navy, lineHeight: 1 }}>{avg.toFixed(2)}</div>
        <div style={{ fontSize: '11px', color: C.muted, marginTop: '4px' }}>average of {q.rating_min}–{q.rating_max}</div>
        <div style={{ fontSize: '11.5px', color: C.textSec, marginTop: '6px' }}>Median {median} · n={vals.length}</div>
      </div>
      <div style={{ flex: 1, minWidth: '260px', height: '160px' }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={dist} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
            <XAxis dataKey="name" tick={{ fontSize: 11, fill: C.muted }} axisLine={{ stroke: C.border }} tickLine={false} />
            <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: C.muted }} axisLine={false} tickLine={false} />
            <Tooltip contentStyle={chartTip} cursor={{ fill: 'rgba(10,22,40,0.04)' }} />
            <Bar dataKey="count" fill={C.navy} radius={[3, 3, 0, 0]} maxBarSize={48}>
              <LabelList dataKey="count" position="top" style={{ fontSize: 11, fill: C.muted }} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function NPSResult({ answers }: { answers: PulseAnswer[] }) {
  const vals = answers.map(a => a.answer_number).filter((n): n is number => n !== null && n !== undefined);
  if (!vals.length) return <EmptyResult />;
  const prom = vals.filter(n => n >= 9).length;
  const pass = vals.filter(n => n >= 7 && n < 9).length;
  const det = vals.filter(n => n < 7).length;
  const total = vals.length;
  const nps = Math.round(((prom - det) / total) * 100);
  const pct = (n: number) => (n / total) * 100;
  const seg = (n: number, col: string, label: string) => n === 0 ? null : (
    <div style={{ width: `${pct(n)}%`, background: col, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.white, fontSize: '11px', fontWeight: 700 }} title={`${label}: ${n}`}>
      {pct(n) > 8 ? `${Math.round(pct(n))}%` : ''}
    </div>
  );
  return (
    <div>
      <div style={{ display: 'flex', gap: '28px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '16px' }}>
        <div style={{ textAlign: 'center', minWidth: '110px' }}>
          <div style={{ fontFamily: 'Georgia, serif', fontSize: '48px', fontWeight: 600, lineHeight: 1, color: nps >= 0 ? C.success : C.danger }}>{nps > 0 ? '+' : ''}{nps}</div>
          <div style={{ fontSize: '11px', color: C.muted, marginTop: '4px', letterSpacing: '0.5px', textTransform: 'uppercase', fontWeight: 700 }}>NPS Score</div>
        </div>
        <div style={{ display: 'flex', gap: '18px' }}>
          {[['Promoters', prom, C.success], ['Passives', pass, C.amber], ['Detractors', det, C.danger]].map(([l, n, c]) => (
            <div key={l as string} style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '22px', fontWeight: 700, color: c as string, fontFamily: 'Georgia, serif' }}>{n as number}</div>
              <div style={{ fontSize: '10.5px', color: C.muted, marginTop: '2px' }}>{l as string}</div>
              <div style={{ fontSize: '10px', color: C.mutedLt }}>{Math.round(pct(n as number))}%</div>
            </div>
          ))}
        </div>
      </div>
      <div style={{ display: 'flex', height: '30px', borderRadius: '4px', overflow: 'hidden', border: `1px solid ${C.border}` }}>
        {seg(det, C.danger, 'Detractors')}
        {seg(pass, C.amber, 'Passives')}
        {seg(prom, C.success, 'Promoters')}
      </div>
      <div style={{ marginTop: '6px', fontSize: '11px', color: C.mutedLt }}>Based on {total} responses. NPS = %Promoters − %Detractors.</div>
    </div>
  );
}

function FreeTextResult({ answers, isPB, nameFor }: { answers: PulseAnswer[]; isPB?: boolean; nameFor?: (id: string) => string }) {
  const quotes = answers.map(a => ({ text: (a.answer_text || '').trim(), rid: a.response_id })).filter(q => q.text);
  if (!quotes.length) return <EmptyResult />;
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '10px' }}>
      {quotes.map((q, i) => (
        <div key={i} style={{ ...card, padding: '13px 15px', background: '#FCFCFD' }}>
          <div style={{ fontSize: '19px', color: C.gold, lineHeight: 0.5, marginBottom: '6px', fontFamily: 'Georgia, serif' }}>“</div>
          <div style={{ fontSize: '12.5px', color: C.text, lineHeight: 1.55 }}>{q.text}</div>
          <div style={{ marginTop: '8px', fontSize: '10.5px', color: C.mutedLt, fontWeight: 600 }}>
            {isPB && nameFor ? `, ${nameFor(q.rid)}` : ', Anonymous'}
          </div>
        </div>
      ))}
    </div>
  );
}

function RankingResult({ q, answers }: { q: PulseQuestion; answers: PulseAnswer[] }) {
  // Average rank (1 = best). Lower is better.
  const totals: Record<string, { sum: number; n: number }> = {};
  q.options.forEach(o => { totals[o] = { sum: 0, n: 0 }; });
  answers.forEach(a => {
    const order = a.answer_options || [];
    order.forEach((item, idx) => {
      if (totals[item]) { totals[item].sum += idx + 1; totals[item].n += 1; }
    });
  });
  const ranked = Object.entries(totals)
    .filter(([, v]) => v.n > 0)
    .map(([name, v]) => ({ name, avg: v.sum / v.n }))
    .sort((a, b) => a.avg - b.avg);
  if (!ranked.length) return <EmptyResult />;
  const worst = Math.max(...ranked.map(r => r.avg));
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '7px' }}>
      {ranked.map((r, i) => (
        <div key={r.name} style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span style={{ width: '26px', height: '26px', flexShrink: 0, borderRadius: '50%', background: i === 0 ? C.gold : C.surface, color: i === 0 ? C.navy : C.textSec, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '12px', fontFamily: 'Georgia, serif', border: `1px solid ${i === 0 ? C.gold : C.border}` }}>{i + 1}</span>
          <span style={{ flex: 1, fontSize: '13px', color: C.text, fontWeight: 500 }}>{r.name}</span>
          <div style={{ width: '120px', height: '8px', background: C.surface, borderRadius: '3px', overflow: 'hidden' }}>
            <div style={{ width: `${100 - ((r.avg - 1) / Math.max(0.001, worst - 1)) * 85}%`, height: '100%', background: C.navy }} />
          </div>
          <span style={{ width: '58px', textAlign: 'right', fontSize: '11.5px', color: C.muted, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{r.avg.toFixed(2)}</span>
        </div>
      ))}
      <div style={{ fontSize: '11px', color: C.mutedLt, marginTop: '2px' }}>Average rank across respondents. Lower = ranked higher.</div>
    </div>
  );
}

function MatrixResult({ q, answers }: { q: PulseQuestion; answers: PulseAnswer[] }) {
  // Column label → 1-based score.
  const colScore: Record<string, number> = {};
  q.matrix_columns.forEach((c, i) => { colScore[c] = i + 1; });
  const maxScore = q.matrix_columns.length;

  // Per row/col counts and per-row average.
  const cellCount: Record<string, Record<string, number>> = {};
  const rowAvg: Record<string, { sum: number; n: number }> = {};
  q.matrix_rows.forEach(r => { cellCount[r] = {}; q.matrix_columns.forEach(c => { cellCount[r][c] = 0; }); rowAvg[r] = { sum: 0, n: 0 }; });
  answers.forEach(a => {
    const m = a.answer_matrix || {};
    q.matrix_rows.forEach(r => {
      const col = m[r];
      if (col && colScore[col]) { cellCount[r][col] += 1; rowAvg[r].sum += colScore[col]; rowAvg[r].n += 1; }
    });
  });
  const anyData = q.matrix_rows.some(r => rowAvg[r].n > 0);
  if (!anyData) return <EmptyResult />;

  // Heat colour: light → navy by normalised average.
  const heat = (avg: number) => {
    const t = maxScore > 1 ? (avg - 1) / (maxScore - 1) : 0; // 0..1
    // interpolate from surface-ish to navy
    const from = [237, 240, 245]; const to = [10, 22, 40];
    const rgb = from.map((f, i) => Math.round(f + (to[i] - f) * t));
    return { bg: `rgb(${rgb.join(',')})`, fg: t > 0.55 ? C.white : C.text };
  };

  return (
    <div style={{ overflowX: 'auto', border: `1px solid ${C.border}`, borderRadius: '4px' }}>
      <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: `${200 + q.matrix_columns.length * 70}px`, fontFamily: 'system-ui' }}>
        <thead>
          <tr>
            <th style={{ textAlign: 'left', padding: '9px 12px', fontSize: '10.5px', color: C.muted, fontWeight: 700, borderBottom: `1px solid ${C.border}`, background: C.surface }} />
            {q.matrix_columns.map(c => <th key={c} style={{ padding: '9px 6px', fontSize: '10.5px', color: C.textSec, fontWeight: 600, textAlign: 'center', borderBottom: `1px solid ${C.border}`, background: C.surface }}>{c}</th>)}
            <th style={{ padding: '9px 10px', fontSize: '10.5px', color: C.navy, fontWeight: 700, textAlign: 'center', borderBottom: `1px solid ${C.border}`, background: C.surface }}>Avg</th>
          </tr>
        </thead>
        <tbody>
          {q.matrix_rows.map(r => {
            const avg = rowAvg[r].n ? rowAvg[r].sum / rowAvg[r].n : 0;
            return (
              <tr key={r}>
                <td style={{ padding: '9px 12px', fontSize: '12px', color: C.text, fontWeight: 500, borderBottom: `1px solid ${C.borderLt}`, whiteSpace: 'nowrap' }}>{r}</td>
                {q.matrix_columns.map(c => {
                  const n = cellCount[r][c];
                  return <td key={c} style={{ textAlign: 'center', padding: '9px 6px', fontSize: '11.5px', color: n ? C.textSec : C.mutedLt, borderBottom: `1px solid ${C.borderLt}`, fontVariantNumeric: 'tabular-nums' }}>{n || '·'}</td>;
                })}
                <td style={{ textAlign: 'center', padding: '9px 10px', borderBottom: `1px solid ${C.borderLt}` }}>
                  {rowAvg[r].n ? <span style={{ ...heat(avg), padding: '3px 8px', borderRadius: '3px', fontSize: '12px', fontWeight: 700, fontFamily: 'Georgia, serif', display: 'inline-block', minWidth: '34px' }}>{avg.toFixed(2)}</span> : <span style={{ color: C.mutedLt }}>-</span>}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function EmptyResult() {
  return <div style={{ padding: '18px', textAlign: 'center', fontSize: '12px', color: C.mutedLt, background: C.surface, borderRadius: '4px' }}>No responses yet for this question.</div>;
}
